import React, { useState, useEffect, useRef } from 'react'
import '../SettingsViews/SettingsListView.scss'
import '../../Wizard2/Wizard.scss'
import './SettingImportExport.scss'
import '../../Accounting/ReceiptValidation/ReceiptValidation.scss'
import { CheckboxList } from '../SettingsViews/SettingsComponents/CheckboxList'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import Export from '@tecuity/sos-icons/react/export'
import Import from '@tecuity/sos-icons/react/import'
import * as api from 'requests'
import Portal from 'components/Portal/Portal'
import { Modal } from 'components/Modal/Modal'
import { useDispatch } from 'react-redux'
import { setErrorToast, setSuccessToast } from '../../../actions'
import FileDownloader from '../../ImageViewer/FileDownloader'
import format from 'date-fns/format'
import { Spinner } from '../../Loaders/Loaders'
import CloudUpload from '@tecuity/sos-icons/build/react-icons/cloud-upload'
import { ContextBar } from '@tecuity/granite-ui'

const MAX_FILE_UPLOAD_SIZE = process.env.MAX_FILE_UPLOAD_SIZE || 10000000
const UPLOAD_FILE_FORMAT_MERGEABLE_ERROR = process.env.UPLOAD_FILE_FORMAT_MERGEABLE_ERROR
const UPLOAD_FILE_SIZE_MERGEABLE_ERROR = process.env.UPLOAD_FILE_SIZE_MERGEABLE_ERROR

const SettingImportExport = (props) => {
  const dispatch = useDispatch()
  const { pages } = props
  const [loading, setLoading] = useState(false)
  const [settingsList, setSettingsList] = useState([])
  const [settingsHierarchy, setSettingsHierarchy] = useState({})
  const [settingIdsToExport, setSettingIdsToExport] = useState([])
  const [importModalOpen, setImportModalOpen] = useState(false)
  // const [files, setFiles] = useState('')
  const [downloadURL, setDownloadURL] = useState(null)
  const [downloadOnNextTick, setDownloadOnNextTick] = useState(false)
  const downloader = useRef(null)

  useEffect(() => {
    api.GET_SETTINGS_EXPORT_PAGES(
      (data) => {
        const hierarchyMap = {}
        data.forEach((page) => {
          hierarchyMap[page.Id] = page.ParentIds
        })
        setSettingsHierarchy(hierarchyMap)
      },
      (err) => {
        console.error(err)
      }
    )
  }, [])

  useEffect(() => {
    const filteredPages = pages
      .filter((page) => page.IsExportable)
      .sort((a, b) => (a.Category > b.Category ? 1 : a.Category < b.Category ? -1 : 0))
      .sort((a, b) => (a.TabName > b.TabName ? 1 : a.TabName < b.TabName ? -1 : 0))
    setSettingsList(
      filteredPages.map((page) => ({
        label: page.Name,
        id: page.SettingsPageId,
        TabName: page.TabName,
      }))
    )
  }, [pages])

  useEffect(() => {
    if (downloadOnNextTick) {
      setDownloadOnNextTick(false)
      downloader.current.download()
    }
  }, [downloadOnNextTick])

  const clickExport = () => {
    if (settingIdsToExport.length === 0) {
      dispatch(setErrorToast('No settings selected, no export created.'))
      return
    }
    setLoading(true)
    api.GET_SETTINGS_EXPORT_JSON(
      settingIdsToExport,
      (exportJson) => {
        // console.log(exportJson)
        const blob = new Blob([JSON.stringify(exportJson, null, 2)], { type: `application/json` })
        const link = window.URL.createObjectURL(blob)
        setDownloadURL(link)
        setDownloadOnNextTick(true)
        setLoading(false)
      },
      (err) => {
        console.error(err)
        dispatch(setErrorToast('Failed to export selected settings'))
        setLoading(false)
      }
    )
  }

  const recursiveAddPageIdToSet = (checkedSet, pageId) => {
    checkedSet.add(pageId)
    settingsHierarchy[pageId].forEach((parentId) => {
      recursiveAddPageIdToSet(checkedSet, parentId)
    })
  }

  const updateCheckedIdsWithHierarchy = (checked) => {
    if (checked.length < settingIdsToExport.length) {
      const itemRemoved = settingIdsToExport.filter((id) => !checked.includes(id))
      const updatedIds = checked.filter((id) => {
        return !settingsHierarchy[id].includes(itemRemoved[0])
      })
      if (updatedIds.length < checked.length)
        dispatch(setSuccessToast('Setting(s) removed due to hierarchy'))
      setSettingIdsToExport(updatedIds)
    } else {
      const checkedSet = new Set()
      checked.forEach((pageId) => {
        recursiveAddPageIdToSet(checkedSet, pageId)
      })
      if (checkedSet.size > checked.length)
        dispatch(setSuccessToast('Setting(s) added due to hierarchy'))
      setSettingIdsToExport(Array.from(checkedSet))
    }
  }

  return (
    <div className="settings-list-view wizard">
      <h2>Setting Import and Export</h2>
      <div className="custom-correspondence-options">
        <ContextButton
          variant="danger"
          onClick={() => {
            setSettingIdsToExport([])
          }}
        >
          Clear
        </ContextButton>
        <ContextButton
          onClick={() => {
            setSettingIdsToExport(settingsList.map((setting) => setting.id))
          }}
        >
          Select All
        </ContextButton>
      </div>
      <div className="content-column-direction">
        <div>
          <CheckboxList
            items={settingsList}
            checkedList={settingIdsToExport || []}
            setActiveChanges={updateCheckedIdsWithHierarchy}
            useWizardCheckbox
          />
        </div>
        <div className="button-wrapper">
          {loading && (
            <div className="loading-type">
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )}
          <ContextButton
            rightIcon={
              <div className="checkmark-wrapper right">
                <Import />
              </div>
            }
            onClick={() => {
              setImportModalOpen(true)
            }}
          >
            Import
          </ContextButton>
          <ContextButton
            variant="success"
            rightIcon={
              <div className="checkmark-wrapper right">
                <Export />
              </div>
            }
            onClick={clickExport}
          >
            Export
          </ContextButton>
        </div>
        <Portal isOpened={importModalOpen}>
          <Modal
            title={'Import Settings JSON file'}
            modalSize="medium"
            cancelButton
            autoHeight
            closeModal={() => {
              setImportModalOpen(false)
            }}
          >
            <div>
              <SettingJSONFileUpload
                settingsList={settingsList}
                closeModal={() => {
                  setImportModalOpen(false)
                }}
              />
            </div>
          </Modal>
        </Portal>
      </div>
      <FileDownloader
        fileName={`settings-export-${format(new Date(), 'MM-dd-yyyy')}.json`}
        href={downloadURL}
        ref={downloader}
      />
    </div>
  )
}

export default SettingImportExport

export const SettingJSONFileUpload = ({ settingsList, closeModal }) => {
  const dispatch = useDispatch()
  // const [progress, setProgress] = useState(0)
  const [droppable, setDroppable] = useState(false)
  const [file, setFile] = useState(null)
  const [isFileValid, setIsFileValid] = useState(true)
  const [importJson, setImportJson] = useState(null)
  const [parsedFileSettingNames, setParsedFileSettingNames] = useState([])
  const [parsedImportErrors, setParsedImportErrors] = useState([])
  const inputRef = useRef(null)

  const showDroppableIndicator = (e) => {
    e.preventDefault()
    setDroppable(true)
  }
  const hideDroppableIndicator = (e) => {
    e.preventDefault()
    setDroppable(false)
  }
  useEffect(() => {
    window.addEventListener('dragover', showDroppableIndicator, false)
    window.addEventListener('drop', hideDroppableIndicator, false)

    return () => {
      window.removeEventListener('dragover', showDroppableIndicator, false)
      window.removeEventListener('drop', hideDroppableIndicator, false)
    }
  }, [])

  const removeFile = (e) => {
    setFile(null)
    setImportJson(null)
    setIsFileValid(true)
    setParsedFileSettingNames([])
    setParsedImportErrors([])
  }

  const handleChange = (e) => {
    // let milliseconds = Date.now()
    let allowedExtensions = ['json']
    let files = [...e.target.files].map((file, i) => {
      const extension = file.name.split('.').pop().toLowerCase()
      let rejectImmediately = false
      let rejectReason = ''
      if (file.size > MAX_FILE_UPLOAD_SIZE) {
        rejectImmediately = true
        rejectReason = UPLOAD_FILE_SIZE_MERGEABLE_ERROR
          ? UPLOAD_FILE_SIZE_MERGEABLE_ERROR.replace(
              '{{FILE_SIZE}}',
              MAX_FILE_UPLOAD_SIZE / 1000000
            )
          : 'Exceeded max file size'
      }
      if (allowedExtensions.every((ext) => ext !== extension)) {
        rejectImmediately = true
        rejectReason = UPLOAD_FILE_FORMAT_MERGEABLE_ERROR
          ? UPLOAD_FILE_FORMAT_MERGEABLE_ERROR.replace(
              '{{FILE_FORMAT}}',
              allowedExtensions.join(', ').toUpperCase()
            )
          : 'Unsupported file format.'
      }
      return {
        file,
        rejectImmediately,
        rejectReason,
        status: 'pending',
        progress: 0,
        filename: file.name,
        '@type': 'file',
        type: file.type,
      }
    })
    setFile(files[0])
  }

  const handleFileDrop = (e) => {
    e.preventDefault()
    let files = []
    for (let i = 0; i < e.dataTransfer.items.length; i++) {
      if (e.dataTransfer.items[i].kind === 'file') {
        const file = e.dataTransfer.items[i].getAsFile()
        if (file) files.push(file)
      }
    }
    if (files.length) handleChange({ target: { files } })
  }

  useEffect(async () => {
    if (!file) return
    const fileText = await file.file.text()
    const json = JSON.parse(fileText)
    if (typeof json !== 'object') {
      dispatch(setErrorToast('Failed to parse setting import file'))
      return
    }
    setImportJson(json)
    const keys = Object.keys(json)
    const settingNames = []
    keys.forEach((settingPageId) => {
      const settingPage = settingsList.find((page) => page.id === +settingPageId)
      if (settingPage) {
        settingNames.push(settingPage.label)
      } else {
        setIsFileValid(false)
        settingNames.push(
          `Setting Page Id: ${settingPageId} not found in current environment. Contact Admin.`
        )
      }
    })
    setParsedFileSettingNames(settingNames)
  }, [file, settingsList])

  const parseErrors = (data) => {
    if (typeof data === 'string') {
      setParsedImportErrors([`Error: ${data}`])
      return
    }
    const keys = Object.keys(data)
    const importErrors = []
    keys.forEach((settingPageId) => {
      const settingPage = settingsList.find((page) => page.id === +settingPageId)
      if (settingPage) {
        importErrors.push(`${settingPage.label}: ${data[settingPageId]}`)
      }
    })
    setParsedImportErrors(importErrors)
    setParsedFileSettingNames([])
  }

  const uploadFileJson = () => {
    if (!importJson) return
    setFile({ ...file, progress: 50, status: 'uploading' })
    api.IMPORT_SETTINGS_JSON(
      importJson,
      (data) => {
        parseErrors(data)
        setFile({ ...file, progress: 100, status: 'success' })
      },
      (err) => {
        console.error(err)
        setFile({ ...file, progress: 100, status: 'failed' })
        parseErrors(err)
        dispatch(setErrorToast('Failed to import settings!'))
      }
    )
  }

  return (
    <div className="condition-editor modal-full-width">
      <div className="file-wrapper">
        <div className="row">
          <div className="modal-full-width">
            {file ? (
              <File
                {...file}
                onRemoveFile={removeFile}
                filename={file ? file.filename : ''}
                // disabled={disabled}
              />
            ) : (
              <div className="modal-full-width">
                <div className="col-xl-7 col-lg-8 col-md-9">
                  <input
                    className="hidden-upload"
                    type="file"
                    name="settingImportUpload"
                    id="settingImportUpload"
                    onChange={handleChange}
                    accept={'application/json'}
                    ref={inputRef}
                  />
                  <label
                    className={`styled-file-upload`}
                    htmlFor="settingImportUpload"
                    onDrop={handleFileDrop}
                  >
                    <span
                      className={`icon icon-upload ${droppable ? 'droppable-animation' : ''}`}
                    />
                    {droppable
                      ? 'Drop file here to upload'
                      : `Select a file to upload (application/json)`}
                  </label>
                </div>
              </div>
            )}
            <div className="setting-import-information">
              {parsedFileSettingNames.length > 0 && parsedImportErrors.length === 0 && (
                <div className="import-settings-list">
                  <h4>Settings imported from this file:</h4>
                  <ul>
                    {parsedFileSettingNames.map((settingName, index) => {
                      return <li key={index}>{settingName}</li>
                    })}
                  </ul>
                </div>
              )}
              {parsedImportErrors.length > 0 && (
                <div className="import-settings-list">
                  <br />
                  <h4>The following errors occurred on import:</h4>
                  <ul>
                    {parsedImportErrors.map((settingName, index) => {
                      return <li key={index}>{settingName}</li>
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
        <ContextBar elevation="standard" className="modal-context-bar">
          <div className="context-bar-content">
            <ContextButton
              variant="secondary"
              onClick={closeModal}
              disabled={file && file.status === 'uploading'}
            >
              Cancel
            </ContextButton>
            <ContextButton
              variant={isFileValid ? 'primary' : 'danger'}
              onClick={uploadFileJson}
              disabled={!importJson || (file && file.status === 'uploading')}
            >
              Upload
            </ContextButton>
          </div>
        </ContextBar>
      </div>
    </div>
  )
}

const File = (props) => {
  const { progress, filename, status, disabled, rejectReason, onRemoveFile } = props
  const success = status === 'success'
  const failed = status === 'failed'
  const uploading = status === 'uploading'

  const removeFile = (e) => {
    onRemoveFile(e)
  }

  return (
    <div className={`progress ${success ? 'success' : ''} ${failed ? 'failed' : ''}`}>
      <div className="file-icon">
        {uploading && <div className="shine-spinner" />}
        <CloudUpload className={`icon ${uploading ? 'spin-out' : 'spin-in'}`} />
      </div>
      <span className="filename">{filename}</span>
      {failed && <span className="failed-text">{rejectReason || 'Upload Failed'}</span>}
      {!disabled
        ? (success || failed) && (
            <button className="remove" onClick={removeFile}>
              <span className="icon icon-close" />
            </button>
          )
        : null}
      <div className="bar" style={{ width: progress + '%' }} />
    </div>
  )
}
